import isNil from 'lodash/isNil';
import { useIsBulkOperationsInProgress } from '@atlassian/jira-bulk-operations-common/src/controllers/use-bulk-operations/index.tsx';
import type { ActiveKeyMap } from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-registry';
import {
	type ExternalAction,
	externalEditFieldAction,
	externalAddCommentAction,
} from '@atlassian/jira-issue-view-store/src/actions/external-actions';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	LabelsKey,
	AssigneeKey,
} from '@atlassian/jira-providers-issue/src/model/issue-system-fields.tsx';
import { useShortcutAnalytics } from '@atlassian/jira-software-keyboard-shortcuts/src/utils/analytics/index.tsx';
import { View } from '@atlassian/jira-software-view-settings/src/common/types/constant.tsx';
import { ViewSetting } from '@atlassian/jira-software-view-settings/src/common/types/settings.tsx';
import { useViewSettingsActions } from '@atlassian/jira-software-view-settings/src/controllers/index.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import { TOP_OF_LIST, BOTTOM_OF_LIST } from '../../../model/constants';
import {
	ASSIGNEE_SHORTCUT_KEY,
	LABELS_SHORTCUT_KEY,
	COMMENT_SHORTCUT_KEY,
} from '../../../model/keyboard-shortcuts/constants';
import type { CardId, CardKey, Person } from '../../../model/types';
import { addSelectedIssueToUrl } from '../../../services/routing/selected-issue-url-change';
import { useBacklogDispatch, useBacklogSelector } from '../../../state';
import { assignToMeRequestThrottled } from '../../../state/actions/cards/assign-to-me';
import { cardsMove } from '../../../state/actions/cards/move';
import { cardNext, cardPrev } from '../../../state/actions/cards/navigate';
import { cardClick } from '../../../state/actions/cards/selection';
import { openDetailView, closeDetailView } from '../../../state/actions/detail-view';
import type { SelectedState } from '../../../state/reducers/ui/cards/selected/types';
import type { DetailViewState } from '../../../state/reducers/ui/detail-view/types';
import { getRapidViewId } from '../../../state/selectors/app';
import {
	getActiveCard,
	getActiveCardKey,
	getDetailViewData,
	makeGetCardListIdByCardId,
	getActiveCardAssignee,
	getSelectedCards,
} from '../../../state/selectors/cards';
import { getShowEpicAsPanel } from '../../../state/selectors/config';
import { canEditIssue } from '../../../state/selectors/permissions';
import { getCurrentUser } from '../../../state/selectors/user';
import { isVersionsFeatureEnabled } from '../../../state/selectors/version';

const bentoShortcutsAnalyticsMap = {
	[ASSIGNEE_SHORTCUT_KEY]: 'assignIssueKeyboardShortcut',
	[LABELS_SHORTCUT_KEY]: 'editIssueLabelsKeyboardShortcut',
	[COMMENT_SHORTCUT_KEY]: 'commentIssueKeyboardShortcut',
} as const;

const bentoShortcutKeys: Array<keyof typeof bentoShortcutsAnalyticsMap> = [
	ASSIGNEE_SHORTCUT_KEY,
	LABELS_SHORTCUT_KEY,
	COMMENT_SHORTCUT_KEY,
];

export const getExternalActionForKey = (key: string): ExternalAction | null => {
	switch (key) {
		case ASSIGNEE_SHORTCUT_KEY:
			return externalEditFieldAction(AssigneeKey);

		case LABELS_SHORTCUT_KEY:
			return externalEditFieldAction(LabelsKey);

		case COMMENT_SHORTCUT_KEY:
			return externalAddCommentAction();

		default:
			return null;
	}
};

export const useKeyMap = (): ActiveKeyMap => {
	const fireShortcutAnalytics = useShortcutAnalytics('backlog');
	const [{ location }, { push }] = useRouter();
	const dispatch = useBacklogDispatch();
	const isBulkOperationsInProgress = useIsBulkOperationsInProgress();
	const onNextIssue = () => {
		if (isBulkOperationsInProgress) {
			return;
		}
		dispatch(cardNext(fireShortcutAnalytics));
	};

	const onPrevIssue = () => {
		if (isBulkOperationsInProgress) {
			return;
		}
		dispatch(cardPrev(fireShortcutAnalytics));
	};

	const onAssignToMe = (
		activeCardId: CardId | undefined,
		activeCardAssignee: Person | null,
		currentUser: Person | null,
	) => {
		if (isNil(activeCardId)) {
			return;
		}
		const assignee = currentUser?.id === activeCardAssignee?.id ? null : currentUser;
		dispatch(assignToMeRequestThrottled(activeCardId, assignee, fireShortcutAnalytics));
	};

	const onOpenFocusedCard = (
		activeCardId: CardId | undefined,
		isBulkOperationsSidebarActive: boolean,
	) => {
		if (isNil(activeCardId) || isBulkOperationsSidebarActive) {
			return;
		}
		dispatch(cardClick(activeCardId, false, false, false));
		fireShortcutAnalytics({ key: 'o', id: 'viewIssueKeyboardShortcut' });
	};

	const onToggleBentoViewForFocusedCard = (
		detailViewData: DetailViewState,
		activeCardId: CardId | undefined,
		isBulkOperationsSidebarActive: boolean,
	) => {
		if (isNil(activeCardId) || isBulkOperationsSidebarActive) {
			return;
		}

		const isDetailsViewAlreadyOpen = detailViewData !== null;
		if (detailViewData !== null && detailViewData.cardId === activeCardId) {
			dispatch(closeDetailView());
		} else {
			dispatch(openDetailView(activeCardId));
		}
		fireShortcutAnalytics({
			key: 't',
			id: 'toggleDetailsViewKeyboardShortcut',
			attributes: {
				toggled: !isDetailsViewAlreadyOpen ? 'open' : 'closed',
			},
		});
	};

	const onOpenAndFocusBentoField = (
		key: keyof typeof bentoShortcutsAnalyticsMap,
		activeCardId: CardId | undefined,
		activeCardKey: CardKey | undefined,
		isBulkOperationsSidebarActive: boolean,
	) => {
		if (isNil(activeCardId) || isNil(activeCardKey) || isBulkOperationsSidebarActive) {
			return;
		}

		const externalAction = getExternalActionForKey(key);
		if (!externalAction) {
			return;
		}

		dispatch(openDetailView(activeCardId, externalAction));
		push(addSelectedIssueToUrl(activeCardKey, location));

		if (bentoShortcutsAnalyticsMap[key]) {
			fireShortcutAnalytics({ key, id: bentoShortcutsAnalyticsMap[key] });
		}
	};

	/*
        Required to create a separate AnalyticsEvent for the cardsMove() action since
        fireShortcutAnalytics is of the wrong type. This analytics event is not used and is simply
        created to satisfy the cardsMove input parameters
    */
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onMoveToTopOrBottomOfContainer = (
		selectedCards: SelectedState,
		originCardListId: string | undefined,
		placement: typeof TOP_OF_LIST | typeof BOTTOM_OF_LIST,
		canUserEditIssues: boolean,
	) => {
		/*
        Adding logic so that if a user only has viewer permissions then they cannot edit the cards
        */

		if (!canUserEditIssues || !selectedCards.length || isNil(originCardListId)) {
			return;
		}

		const analyticsEvent = createAnalyticsEvent({
			actionSubject: 'keyboardShortcut',
			action: 'pressed',
		});

		dispatch(
			cardsMove(
				{
					cardListId: originCardListId,
					placement,
					isBetweenLists: false,
					isFromActiveSprint: false,
					cardIds: selectedCards,
				},
				analyticsEvent,
			),
		);
		const isTop = placement === TOP_OF_LIST;

		const attributes = {
			key: isTop ? 's t' : 's b',
			id: isTop ? 'sendToTopKeyboardShortcut' : 'sendToBottomKeyboardShortcut',
			attributes: {
				isMultiSelect: selectedCards.length > 1,
			},
		};
		fireShortcutAnalytics(attributes);
	};

	const selectedCards = useBacklogSelector(getSelectedCards);
	const selectedCardId =
		selectedCards.length !== 0 ? selectedCards[selectedCards.length - 1] : undefined;

	const activeCardId = useBacklogSelector(getActiveCard);
	const activeCardKey = useBacklogSelector(getActiveCardKey);
	const detailViewData = useBacklogSelector(getDetailViewData);
	const activeCardAssignee = useBacklogSelector(getActiveCardAssignee);
	const currentUserAccount = useBacklogSelector(getCurrentUser);
	const originCardListId = useBacklogSelector((state) =>
		!isNil(selectedCardId) ? makeGetCardListIdByCardId(state)(selectedCardId) : undefined,
	);
	const canUserEditIssues = useBacklogSelector(canEditIssue);
	const isVersionsEnabled = useBacklogSelector(isVersionsFeatureEnabled);
	const isEpicsPanelEnabled = useBacklogSelector(getShowEpicAsPanel);
	const rapidViewId = useBacklogSelector(getRapidViewId);
	const { toggleViewSetting } = useViewSettingsActions();

	const onEpicsPanelToggle = () => {
		if (!isEpicsPanelEnabled) {
			return;
		}
		toggleViewSetting({
			boardId: Number(rapidViewId),
			setting: ViewSetting.EPIC_PANEL,
			view: View.BACKLOG,
		});
		fireShortcutAnalytics({ key: 'e', id: 'epicPanelToggleKeyboardShortcut' });
	};

	const onVersionsPanelToggle = () => {
		if (!isVersionsEnabled) {
			return;
		}
		toggleViewSetting({
			boardId: Number(rapidViewId),
			setting: ViewSetting.VERSION_PANEL,
			view: View.BACKLOG,
		});
		fireShortcutAnalytics({ key: 'v', id: 'versionPanelToggleKeyboardShortcut' });
	};

	const keyMap: ActiveKeyMap = {
		o: {
			callback: () => {
				onOpenFocusedCard(activeCardId, isBulkOperationsInProgress);
			},
		},
		j: {
			callback: onNextIssue,
		},
		k: {
			callback: onPrevIssue,
		},
		t: {
			callback: () => {
				onToggleBentoViewForFocusedCard(detailViewData, activeCardId, isBulkOperationsInProgress);
			},
		},
		i: {
			callback: () => {
				onAssignToMe(activeCardId, activeCardAssignee, currentUserAccount);
			},
		},
		's t': {
			callback: () => {
				onMoveToTopOrBottomOfContainer(
					selectedCards,
					originCardListId,
					TOP_OF_LIST,
					canUserEditIssues,
				);
			},
		},
		's b': {
			callback: () => {
				onMoveToTopOrBottomOfContainer(
					selectedCards,
					originCardListId,
					BOTTOM_OF_LIST,
					canUserEditIssues,
				);
			},
		},
		e: {
			callback: onEpicsPanelToggle,
		},
		v: {
			callback: onVersionsPanelToggle,
		},
	};

	bentoShortcutKeys.forEach((key) => {
		keyMap[key] = {
			callback: () => {
				onOpenAndFocusBentoField(key, activeCardId, activeCardKey, isBulkOperationsInProgress);
			},
		};
	});

	return keyMap;
};
